import * as React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import {billablesAdjust, formatMoney} from "./billablesService";

export type AdjustBillablesContext = {
    school: string;
    schoolGroupId: number;
    companyId: number;
    brokerId: number;
    year: number;
    month: number;
    expectedBilled: number;
};

export default function AdjustBillablesModal(props: {
    context: AdjustBillablesContext | null;
    onClose: () => void;
    onSaved: (ctx: AdjustBillablesContext) => void;
}) {
    const [newAmount, setNewAmount] = React.useState("");
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        setNewAmount("");
        setError("");
        setSaving(false);
    }, [props.context]);

    if (!props.context) {
        return null;
    }

    const ctx = props.context;

    const handleClose = (_?: any, reason?: string) => {
        if (reason === "backdropClick" || saving) {
            return;
        }
        props.onClose();
    };

    const save = async () => {
        const amount = Number(newAmount);
        if (!Number.isFinite(amount)) {
            setError("Enter a valid amount.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const result = await billablesAdjust({
                schoolGroupId: ctx.schoolGroupId,
                companyId: ctx.companyId,
                brokerId: ctx.brokerId,
                year: ctx.year,
                month: ctx.month,
                expectedBilled: ctx.expectedBilled,
                newAmount: amount,
            });
            if (!result) {
                setError("Unable to adjust billables.");
                setSaving(false);
                return;
            }
            toast.success(
                `Adjusted by ${formatMoney(result.addition)} per lead (${result.leads.toLocaleString()} leads)`
            );
            setSaving(false);
            props.onClose();
            props.onSaved(ctx);
        } catch (e: any) {
            setError(e?.message || "Unable to adjust billables.");
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!props.context} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Adjust Billables</DialogTitle>
            <DialogContent>
                <Typography sx={{mb: 1}}>
                    <b>{ctx.school}</b> ({ctx.schoolGroupId})
                    <br />
                    {ctx.month}/{ctx.year}
                </Typography>
                <Typography sx={{mb: 2}}>
                    Current amount: <b>{formatMoney(ctx.expectedBilled)}</b>
                </Typography>
                <TextField
                    label="New amount"
                    type="number"
                    fullWidth
                    size="small"
                    autoFocus
                    inputProps={{step: "0.01"}}
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                    disabled={saving}
                />
                {error ? (
                    <Typography color="error" sx={{mt: 1, fontSize: 13}}>
                        {error}
                    </Typography>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.onClose()} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={saving || newAmount === ""}
                >
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
